import { logger } from '../utils/logger';
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Tab, 
  Tabs, 
  Grid,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Tooltip,
  Avatar,
  Stack,
  LinearProgress,
  Popover
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Receipt,
  AttachMoney,
  AccountBalance,
  Assessment,
  Close,
  Visibility,
  Download,
  Share,
  Launch,
  LocalShipping,
  Person,
  LocationOn,
  DateRange,
  CreditCard,
  Store,
  ShoppingBag,
  BarChart,
  Refresh,
  Info as InfoIcon,
  Calculate,
  GetApp,
  Email
} from '@mui/icons-material';
import { getAuthHeader } from '../utils/auth';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import CompanySelector from './CompanySelector';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../services/api';

// Tax Explanation Component
const TaxExplanationTooltip = ({ title, explanation, children }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        {children}
        <Tooltip title={`Click for explanation: ${title}`}>
          <IconButton size="small" onClick={handleClick} sx={{ color: 'primary.main' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { maxWidth: 400, p: 2 }
        }}
      >
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {explanation}
        </Typography>
      </Popover>
    </>
  );
};

// Helper function to safely get transaction amount
const getTransactionAmount = (transaction) => {
  return transaction.transaction_amount || transaction.amount || 0;
};

// Helper function to get actual tax
const getActualTax = (transaction) => {
  return transaction.actual_tax ?? transaction.imported_tax_amount ?? 0;
};

// Helper function to get expected tax
const getExpectedTax = (transaction) => {
  return transaction.expected_tax ?? transaction.expected_tax_due ?? transaction.tax_amount ?? 0;
};

// Helper function to get variance amount
const getVarianceAmount = (transaction) => {
  return transaction.variance_amount ?? (getExpectedTax(transaction) - getActualTax(transaction));
};

// Export transaction data
const exportTransactionData = (transaction) => {
  const exportData = {
    transaction_id: transaction.platform_transaction_id,
    transaction_date: transaction.transaction_date,
    transaction_amount: getTransactionAmount(transaction),
    actual_tax_collected: getActualTax(transaction),
    expected_tax_due: getExpectedTax(transaction),
    variance_amount: getVarianceAmount(transaction),
    variance_type: transaction.variance_type,
    filing_period: transaction.filing_period,
    customer_name: transaction.customer_name,
    customer_email: transaction.customer_email,
    billing_address: transaction.billing_address,
    shipping_address: transaction.shipping_address,
    line_items: transaction.items || transaction.raw_data?.line_items || []
  };

  const dataStr = JSON.stringify(exportData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `transaction_${transaction.platform_transaction_id}_details.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Open tax calculation details
const openTaxCalculationDetails = (transaction) => {
  const calcWindow = window.open('', '_blank', 'width=800,height=600');
  const actualTax = getActualTax(transaction);
  const expectedTax = getExpectedTax(transaction);
  const variance = getVarianceAmount(transaction);
  const transactionAmount = getTransactionAmount(transaction);
  
  const taxRate = transactionAmount > 0 ? (expectedTax / transactionAmount * 100) : 0;
  const actualRate = transactionAmount > 0 ? (actualTax / transactionAmount * 100) : 0;
  
  calcWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Tax Calculation Details - ${transaction.platform_transaction_id}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
        .header { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
        .section { margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
        .calculation { background: #fff9c4; padding: 10px; border-radius: 4px; margin: 10px 0; }
        .variance { background: ${variance > 0 ? '#ffebee' : variance < 0 ? '#e8f5e8' : '#f5f5f5'}; padding: 10px; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #f2f2f2; }
        .highlight { background: #fff3cd; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Tax Calculation Details</h1>
        <h2>Transaction: ${transaction.platform_transaction_id}</h2>
        <p><strong>Date:</strong> ${new Date(transaction.transaction_date).toLocaleDateString()}</p>
        <p><strong>Filing Period:</strong> ${transaction.filing_period}</p>
      </div>
      
      <div class="section">
        <h3>Transaction Summary</h3>
        <table>
          <tr><td><strong>Transaction Amount</strong></td><td>$${transactionAmount.toFixed(2)}</td></tr>
          <tr><td><strong>Actual Tax Collected</strong></td><td>$${actualTax.toFixed(2)}</td></tr>
          <tr><td><strong>Expected Tax Due</strong></td><td>$${expectedTax.toFixed(2)}</td></tr>
          <tr class="highlight"><td><strong>Variance</strong></td><td>$${variance.toFixed(2)} (${variance > 0 ? 'Under-collected' : variance < 0 ? 'Over-collected' : 'Perfect match'})</td></tr>
        </table>
      </div>
      
      <div class="section">
        <h3>Tax Rate Analysis</h3>
        <div class="calculation">
          <p><strong>Expected Tax Rate Calculation:</strong></p>
          <p>Expected Tax Rate = Expected Tax ÷ Transaction Amount × 100</p>
          <p>Expected Tax Rate = $${expectedTax.toFixed(2)} ÷ $${transactionAmount.toFixed(2)} × 100 = ${taxRate.toFixed(2)}%</p>
        </div>
        
        <div class="calculation">
          <p><strong>Actual Tax Rate Calculation:</strong></p>
          <p>Actual Tax Rate = Actual Tax ÷ Transaction Amount × 100</p>
          <p>Actual Tax Rate = $${actualTax.toFixed(2)} ÷ $${transactionAmount.toFixed(2)} × 100 = ${actualRate.toFixed(2)}%</p>
        </div>
        
        <div class="variance">
          <p><strong>Rate Variance:</strong> ${(actualRate - taxRate).toFixed(2)} percentage points</p>
          <p><strong>Variance Type:</strong> ${transaction.variance_type || 'Unknown'}</p>
          ${transaction.is_significant ? '<p><strong>⚠️ This is a significant variance that may require attention</strong></p>' : ''}
        </div>
      </div>
      
      <div class="section">
        <h3>Address Information</h3>
        ${transaction.shipping_address ? `
          <h4>Shipping Address (Used for Tax Calculation):</h4>
          <p>${transaction.shipping_address.address1 || ''}<br>
          ${transaction.shipping_address.address2 ? transaction.shipping_address.address2 + '<br>' : ''}
          ${transaction.shipping_address.city || ''}, ${transaction.shipping_address.province || transaction.shipping_address.state || ''} ${transaction.shipping_address.zip || transaction.shipping_address.postal_code || ''}<br>
          ${transaction.shipping_address.country || 'US'}</p>
        ` : ''}
        
        ${transaction.billing_address ? `
          <h4>Billing Address:</h4>
          <p>${transaction.billing_address.address1 || ''}<br>
          ${transaction.billing_address.address2 ? transaction.billing_address.address2 + '<br>' : ''}
          ${transaction.billing_address.city || ''}, ${transaction.billing_address.province || transaction.billing_address.state || ''} ${transaction.billing_address.zip || transaction.billing_address.postal_code || ''}<br>
          ${transaction.billing_address.country || 'US'}</p>
        ` : ''}
      </div>
      
      ${transaction.items || transaction.raw_data?.line_items ? `
        <div class="section">
          <h3>Line Items</h3>
          <table>
            <thead>
              <tr><th>Product</th><th>Quantity</th><th>Unit Price</th><th>Total</th><th>Tax</th></tr>
            </thead>
            <tbody>
              ${(transaction.items || transaction.raw_data?.line_items || []).map(item => `
                                 <tr>
                   <td>${item.title || item.name || 'Product'}</td>
                   <td>${item.quantity || 1}</td>
                   <td>$${Number(item.price || 0).toFixed(2)}</td>
                   <td>$${(Number(item.price || 0) * Number(item.quantity || 1)).toFixed(2)}</td>
                   <td>$${Number(item.tax_amount || 0).toFixed(2)}</td>
                 </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}
      
      <div class="section">
        <h3>How Tax Calculation Works</h3>
        <p><strong>Expected Tax Calculation:</strong> GoTruTax calculates the expected tax based on:</p>
        <ul>
          <li>Transaction amount and line items</li>
          <li>Shipping/billing address (determines tax jurisdiction)</li>
          <li>Current tax rates for the jurisdiction</li>
          <li>Product taxability rules</li>
        </ul>
        
        <p><strong>Actual Tax:</strong> The amount of tax that was actually collected during the transaction.</p>
        
        <p><strong>Variance Analysis:</strong> We compare actual vs expected to identify:</p>
        <ul>
          <li><strong>Under-collection:</strong> Less tax collected than required</li>
          <li><strong>Over-collection:</strong> More tax collected than required</li>
          <li><strong>Penny variance:</strong> Minor differences due to rounding</li>
        </ul>
      </div>
      
      <button onclick="window.print()" style="background: #1976d2; color: white; padding: 10px 20px; border: none; border-radius: 4px; cursor: pointer; margin-top: 20px;">Print Report</button>
    </body>
    </html>
  `);
  calcWindow.document.close();
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TransactionDetailsModal = ({ open, onClose, transaction, onNavigateToCalculation, loading = false }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    } catch (e) {
      return dateString;
    }
  };

  const getVarianceColor = (variance) => {
    if (variance > 0.5) return 'error.main';  // Significant under-collect
    if (variance < -0.5) return 'info.main';  // Significant over-collect
    if (variance !== 0) return 'warning.main';  // Penny variance
    return 'success.main';  // Exact match
  };

  const getVarianceIcon = (varianceType) => {
    switch (varianceType) {
      case 'under_collected':
        return <Warning color="warning" />;
      case 'over_collected':
        return <AccountBalance color="info" />;
      case 'penny_variance':
        return <AttachMoney color="secondary" />;
      default:
        return <CheckCircle color="success" />;
    }
};

  const calculateLineItemTax = (item) => {
    const price = parseFloat(item.price || item.unit_price || 0);
    const quantity = parseInt(item.quantity || 1, 10);
    const taxRate = parseFloat(item.tax_rate || 0.08); // Default to 8% if not available
    return (price * quantity * taxRate).toFixed(2);
  };

  if (!transaction) {
    return null;
  }

  // Extract data from transaction - check both direct properties and raw_data
  const raw = transaction.raw_data || {};
  const transactionAmount = transaction.transaction_amount || transaction.amount || raw.amount || 0;
  const actualTax = transaction.actual_tax || transaction.tax_amount || raw.tax_amount || 0;
  const expectedTax = transaction.expected_tax || transaction.expected_tax_due || 0;
  const calculatedTax = transaction.calculated_tax ?? actualTax;
  const sourceTax = transaction.source_tax ?? expectedTax;
  const variance = transaction.variance_amount ?? (calculatedTax - sourceTax);
  
  // Get line items from different possible locations
  const lineItems = transaction.items || 
                   raw.line_items || 
                   (raw.data && raw.data.line_items) || 
                   [];
                   
  // Get addresses from different possible locations
  const shippingAddress = transaction.shipping_address || 
                         raw.shipping_address || 
                         (raw.data && raw.data.shipping_address) || 
                         {};
                         
  const billingAddress = transaction.billing_address || 
                        raw.billing_address || 
                        (raw.data && raw.data.billing_address) || 
                        {};
                        
  // Get customer info from different possible locations
  const customerName = transaction.customer_name || 
                      (raw.customer && raw.customer.name) || 
                      (raw.data && raw.data.customer && raw.data.customer.name) || 
                      'N/A';
                      
  const customerEmail = transaction.customer_email || 
                       (raw.customer && raw.customer.email) || 
                       (raw.data && raw.data.customer && raw.data.customer.email) || 
                       'N/A';
                       
  const transactionId = transaction.platform_transaction_id || 
                       transaction.transaction_id || 
                       raw.id || 
                       'N/A';
                       
  const transactionDate = transaction.transaction_date || 
                         transaction.created_at || 
                         raw.created || 
                         new Date().toISOString();
                         
  const filingPeriod = transaction.filing_period || 'N/A';
  const state = shippingAddress.state || 'N/A';
  const varianceType = transaction.variance_type || 'exact_match';

  // Log transaction data for debugging
  logger.log('Rendering transaction details:', {
    transaction,
    raw,
    transactionAmount,
    actualTax,
    expectedTax,
    lineItems,
    transactionId
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Transaction Details</Typography>
            <Chip 
              label={transactionId} 
              color="primary" 
              size="small" 
              variant="outlined" 
              sx={{ fontFamily: 'monospace' }} 
            />
            {transaction.calculation_method && (
              <Chip
                label={transaction.calculation_method}
                size="small"
                variant="outlined"
                color="secondary"
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}
        <Grid container spacing={3}>
          {/* Transaction Summary */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                  <Typography variant="h5" fontWeight="bold">
                          {formatCurrency(transactionAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                    {formatDate(transactionDate)} • Filing Period: {filingPeriod}
                      </Typography>
                    </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                  {getVarianceIcon(varianceType)}
                    <Chip
                    label={getVarianceTypeDisplay(varianceType)} 
                      color={
                      varianceType === 'under_collected' ? 'warning' :
                      varianceType === 'over_collected' ? 'info' :
                      varianceType === 'penny_variance' ? 'secondary' : 'success'
                    }
                    />
                  </Box>
                    </Box>
              
              <Divider sx={{ my: 2 }} />
              
                <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">GoTruTax (calculated)</Typography>
                  <Typography variant="body1" fontWeight="medium">{formatCurrency(calculatedTax)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Source Tax (imported)</Typography>
                  <Typography variant="body1" fontWeight="medium">{formatCurrency(sourceTax)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Variance</Typography>
                  <Typography variant="body1" fontWeight="medium" color={getVarianceColor(variance)}>
                    {variance > 0 ? '+' : ''}{formatCurrency(variance)}
                    </Typography>
                  </Grid>
          </Grid>

              <Box display="flex" gap={1} mt={2}>
                <Button 
                  size="small" 
                  startIcon={<Calculate />} 
                      variant="outlined"
                  onClick={() => openTaxCalculationDetails(transaction)}
                >
                  View Tax Calculation
                </Button>
                <Button 
                      size="small"
                  startIcon={<GetApp />} 
                  variant="outlined"
                  onClick={() => exportTransactionData(transaction)}
                >
                  Export Data
                </Button>
                  </Box>
            </Paper>
          </Grid>

          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Customer Information
                </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Name" 
                    secondary={customerName} 
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Email fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email" 
                    secondary={customerEmail}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Address Information */}
            <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Address Information
                  </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <LocalShipping fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                    Shipping Address
                      </Typography>
                  <Typography variant="body2">
                    {shippingAddress.line1 || 'N/A'}<br />
                    {shippingAddress.city || 'N/A'}, {shippingAddress.state || 'N/A'} {shippingAddress.postal_code || 'N/A'}<br />
                    {shippingAddress.country || 'US'}
                      </Typography>
            </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <LocationOn fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                    Billing Address
                  </Typography>
                  <Typography variant="body2">
                    {billingAddress.line1 || 'N/A'}<br />
                    {billingAddress.city || 'N/A'}, {billingAddress.state || 'N/A'} {billingAddress.postal_code || 'N/A'}<br />
                    {billingAddress.country || 'US'}
                  </Typography>
            </Grid>
            </Grid>
            </Paper>
          </Grid>

          {/* Line Items */}
            <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Line Items
                  </Typography>
              {lineItems && lineItems.length > 0 ? (
                <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Tax</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {lineItems.map((item, index) => {
                        const price = parseFloat(item.price || item.unit_price || 0);
                        const quantity = parseInt(item.quantity || 1, 10);
                        const total = price * quantity;
                        const tax = parseFloat(calculateLineItemTax(item));
                        
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2">
                                {item.name || item.title || `Item ${index + 1}`}
                                </Typography>
                                {item.sku && (
                                <Typography variant="caption" color="text.secondary">
                                    SKU: {item.sku}
                                  </Typography>
                                )}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(price)}</TableCell>
                            <TableCell align="right">{quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(total)}</TableCell>
                            <TableCell align="right">{formatCurrency(tax)}</TableCell>
                          </TableRow>
                        );
                      })}
                      </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={3} />
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">Subtotal:</Typography>
                            </TableCell>
                            <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(transactionAmount - actualTax)}
                          </Typography>
                            </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3} />
                            <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">Tax:</Typography>
                            </TableCell>
                            <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(actualTax)}
                          </Typography>
                            </TableCell>
                          </TableRow>
                        <TableRow>
                        <TableCell colSpan={3} />
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">Total:</Typography>
                          </TableCell>
                          <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(transactionAmount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                    </TableFooter>
                    </Table>
                  </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No line items available for this transaction.
                  </Typography>
          )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const getVarianceTypeDisplay = (type = 'exact_match') => {
  switch (type) {
    case 'under_collected':
      return 'Under-collected';
    case 'over_collected':
      return 'Over-collected';
    case 'penny_variance':
      return 'Penny variance';
    default:
      return 'Exact match';
  }
};

const formatFilingPeriodLabel = (period) => {
  if (!period) return 'Custom period';
  try {
    const date = new Date(`${period}-01T00:00:00`);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } catch (error) {
    return period;
  }
};



// All Transactions Modal Component
const AllTransactionsModal = ({ open, onClose, transactions, onTransactionClick, varianceType }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
        day: 'numeric',
    });
    } catch (e) {
      return dateString;
    }
  };

  const getVarianceColor = (variance) => {
    if (variance > 0.5) return 'error.main';  // Significant under-collect
    if (variance < -0.5) return 'info.main';  // Significant over-collect
    if (variance !== 0) return 'warning.main';  // Penny variance
    return 'success.main';  // Exact match
  };

  const getVarianceIcon = (varianceType) => {
    switch (varianceType) {
      case 'under_collected':
        return <Warning color="warning" />;
      case 'over_collected':
        return <AccountBalance color="info" />;
      case 'penny_variance':
        return <AttachMoney color="secondary" />;
      default:
        return <CheckCircle color="success" />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '80vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {varianceType ? `${getVarianceTypeDisplay(varianceType)} Transactions` : 'All Transactions'}
            {transactions?.length > 0 && ` (${transactions.length})`}
          </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {transactions?.length > 0 ? (
          <TableContainer component={Paper} sx={{ height: '100%' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>GoTruTax Tax</TableCell>
                  <TableCell>Source Tax</TableCell>
                  <TableCell>Variance</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Integration</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => {
                  const calculatedTax = transaction.calculated_tax ?? transaction.actual_tax ?? 0;
                  const sourceTax = transaction.source_tax ?? transaction.expected_tax ?? 0;
                  const variance = transaction.variance_amount ?? (calculatedTax - sourceTax);
                  const state = transaction.shipping_state || 
                                (transaction.shipping_address?.state) || 
                                (transaction.raw_data?.shipping_address?.state) || 
                                'N/A';
                  
                  return (
                    <TableRow 
                      key={transaction.id || transaction.transaction_id} 
                      hover
                      onClick={() => onTransactionClick(transaction)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {transaction.platform_transaction_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(transaction.transaction_date)}</TableCell>
                      <TableCell>{formatCurrency(transaction.transaction_amount || transaction.amount)}</TableCell>
                      <TableCell>{formatCurrency(calculatedTax)}</TableCell>
                      <TableCell>{formatCurrency(sourceTax)}</TableCell>
                      <TableCell>
                        <Typography color={getVarianceColor(variance)}>
                          {formatCurrency(variance)}
                        </Typography>
                      </TableCell>
                      <TableCell>{state}</TableCell>
                      <TableCell>{(transaction.integration || '').toString().toUpperCase() || '—'}</TableCell>
                      <TableCell>
                        <Tooltip title={transaction.tax_reason || ''}>
                          <span>{transaction.tax_reason || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={getVarianceTypeDisplay(transaction.variance_type)} 
                          color={
                            transaction.variance_type === 'under_collected' ? 'warning' :
                            transaction.variance_type === 'over_collected' ? 'info' :
                            transaction.variance_type === 'penny_variance' ? 'secondary' : 'success'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View details">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                  onTransactionClick(transaction);
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="body1" color="text.secondary">
              No transactions found.
                    </Typography>
                  </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const TransactionInsights = ({ companyId: propCompanyId }) => {
  const navigate = useNavigate();
  const { companyId: urlCompanyId } = useParams();
  const [searchParams] = useSearchParams();
  const stateFilter = searchParams.get('state');
  const { user } = useAuth();
  // Use companyId from URL, then prop, then user context, then fallback to 1
  const [companyId, setCompanyId] = useState(
    urlCompanyId ? parseInt(urlCompanyId) :
    propCompanyId ? propCompanyId :
    user && user.primary_company_id ? user.primary_company_id : 1
  );
  const [tabValue, setTabValue] = useState(2); // Start with Transaction Insights tab
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filingPeriod, setFilingPeriod] = useState(searchParams.get('filing_period') || '');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [allTransactions, setAllTransactions] = useState([]);
  const [allTransactionsVarianceType, setAllTransactionsVarianceType] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [allTransactionsLoading, setAllTransactionsLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const nexusPath = companyId ? `/company/${companyId}/nexus/dashboard` : '/nexus/dashboard';

  const yearOptions = useMemo(() => {
    const years = Array.from(new Set(availablePeriods.map((period) => period.value.split('-')[0])));
    return years.sort((a, b) => b.localeCompare(a));
  }, [availablePeriods]);

  const periodOptions = useMemo(() => {
    const merged = [...availablePeriods];
    if (filingPeriod && !merged.some((period) => period.value === filingPeriod)) {
      merged.push({
        value: filingPeriod,
        label: formatFilingPeriodLabel(filingPeriod),
      });
    }
    const sorted = merged.sort((a, b) => (a.value > b.value ? -1 : 1));
    if (selectedYear === 'all') {
      return sorted;
    }
    return sorted.filter((period) => period.value.startsWith(`${selectedYear}-`));
  }, [availablePeriods, filingPeriod, selectedYear]);

  useEffect(() => {
    if (yearOptions.length === 0) {
      setSelectedYear('all');
      return;
    }
    if (selectedYear !== 'all' && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions, selectedYear]);

  useEffect(() => {
    if (!periodOptions.length) {
      return;
    }
    if (!filingPeriod || !periodOptions.some((period) => period.value === filingPeriod)) {
      setFilingPeriod(periodOptions[0].value);
    }
  }, [periodOptions, filingPeriod]);

  // Define filing periods for dropdown
  useEffect(() => {
    // If user context changes and companyId is not set from URL or prop, update it
    if (!urlCompanyId && !propCompanyId && user && user.primary_company_id && companyId !== user.primary_company_id) {
      setCompanyId(user.primary_company_id);
    }
  }, [user, urlCompanyId, propCompanyId]);

  useEffect(() => {
    logger.log('TransactionInsights - companyId or filingPeriod changed:', { companyId, filingPeriod });
    if (companyId) {
    fetchInsights();
      fetchCompanyInfo();
    }
  }, [companyId, filingPeriod, stateFilter]);

  const handleFilingPeriodChange = (value) => {
    setFilingPeriod(value);
    if (value) {
      const newYear = value.split('-')[0];
      setSelectedYear(newYear || 'all');
    }
  };

  const handleCompanyChange = (newCompanyId) => {
    logger.log('Company changed to:', newCompanyId);
    setLoading(true); // Set loading immediately when company changes
    setCompanyId(newCompanyId);
    setError(null); // Clear any previous errors
    setAvailablePeriods([]);
    setFilingPeriod('');
    setSelectedYear('all');
    // Update the URL to reflect the selected company
    navigate(`/company/${newCompanyId}/transaction-insights`);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsLoading(false);
  };

  const fetchCompanyInfo = async () => {
    try {
      logger.log('Fetching company info for ID:', companyId);
      const response = await fetch(`${API_URL}/api/v1/companies/${companyId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });
      
      if (response.ok) {
        const company = await response.json();
        logger.log('Company info received:', company);
        setCompanyInfo(company);
      } else {
        logger.error('Failed to fetch company info:', response.status);
        setCompanyInfo(null);
      }
    } catch (err) {
      logger.error('Error fetching company info:', err);
      setCompanyInfo(null);
    }
  };

  const fetchInsights = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filingPeriod) {
        params.append('filing_period', filingPeriod);
      }
      params.append('lookback_period', '12');
      if (stateFilter) params.append('state', stateFilter);

      const url = `${API_URL}/api/v1/tax-variance/insights/${companyId}?${params.toString()}`;
      logger.log('[TransactionInsights] Fetching insights from', url);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      logger.log('Insights data:', data);
      setInsights(data);

      if (Array.isArray(data.available_periods)) {
        setAvailablePeriods(data.available_periods);
      }
      if (!filingPeriod && data.filing_period) {
        setFilingPeriod(data.filing_period);
      }

      if (!companyInfo) {
        fetchCompanyInfo();
      }
    } catch (err) {
      logger.error('Error fetching insights:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTransactions = async (varianceType = null) => {
    if (!companyId) return;
    const periodForQuery = filingPeriod || insights?.filing_period;
    if (!periodForQuery) return;

    try {
      setAllTransactionsLoading(true);
      setAllTransactionsVarianceType(varianceType);

      const params = new URLSearchParams({
        filing_period: periodForQuery,
        lookback_period: '12',
        limit: '100',
      });
      if (varianceType) {
        params.append('variance_type', varianceType);
      }
      if (stateFilter) params.append('state', stateFilter);

      const url = `${API_URL}/api/v1/tax-variance/insights/${companyId}/transactions?${params.toString()}`;
      logger.log('Fetching all transactions from:', url);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });

      if (response.ok) {
        const data = await response.json();
        logger.log('All transactions data:', data);
        setAllTransactions(data.transactions || []);
        setShowAllTransactions(true);
      } else {
        throw new Error(`Failed to fetch transactions: ${response.status}`);
      }
    } catch (err) {
      logger.error('Error fetching all transactions:', err);
      setError(err.message);
    } finally {
      setAllTransactionsLoading(false);
    }
  };

  const handleTransactionClick = async (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);

    if (transaction.items || transaction.raw_data) {
      setDetailsLoading(false);
      return;
    }

    try {
      setDetailsLoading(true);
      logger.log('Fetching transaction details for:', transaction.platform_transaction_id);
      const transactionId = transaction.platform_transaction_id || transaction.id;
      const params = new URLSearchParams({
        filing_period: filingPeriod || insights?.filing_period || '',
        lookback_period: '12',
      });

      const response = await fetch(
        `${API_URL}/api/v1/tax-variance/insights/${companyId}/transactions/${transactionId}?${params.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          }
        }
      );
      
      if (response.ok) {
        const fullTransaction = await response.json();
        logger.log('Transaction details response:', fullTransaction);
        setSelectedTransaction(fullTransaction || transaction);
      } else {
        logger.warn(`Failed to fetch transaction details: ${response.status}`);
        setSelectedTransaction(transaction);
      }
    } catch (err) {
      logger.error('Error fetching transaction details:', err);
      setSelectedTransaction(transaction);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatTrend = (trend) => {
    if (!trend || trend === 0) return '0%';
    const sign = trend > 0 ? '+' : '';
    return `${sign}${trend.toFixed(1)}%`;
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <TrendingUp color="success" />;
    if (trend < 0) return <TrendingDown color="error" />;
    return null;
  };

  const getVarianceIcon = (varianceType) => {
    switch (varianceType) {
      case 'under_collected':
        return <Warning color="warning" />;
      case 'over_collected':
        return <AccountBalance color="info" />;
      case 'penny_variance':
        return <AttachMoney color="secondary" />;
      default:
        return <CheckCircle color="success" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading transaction insights...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading transaction insights: {error}
        <Button onClick={fetchInsights} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, mb: 3 }}>
        <Typography variant="h4" component="h1">
          Transaction Insights
            {companyInfo && (
            <Typography variant="subtitle1" component="span" color="text.secondary" sx={{ ml: 1 }}>
              {companyInfo.name}
              </Typography>
            )}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, width: '100%', minWidth: 0 }}>
            <CompanySelector 
              selectedCompanyId={companyId} 
              onCompanyChange={handleCompanyChange}
            style={{ minWidth: '200px' }}
            />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            size="small"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <MenuItem value="all">All Years</MenuItem>
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Filing Period</InputLabel>
          <Select
            value={filingPeriod || ''}
            label="Filing Period"
            size="small"
            onChange={(e) => handleFilingPeriodChange(e.target.value)}
          >
            {!periodOptions.length && (
              <MenuItem value="" disabled>
                No filing periods available
              </MenuItem>
            )}
            {periodOptions.map((period) => (
              <MenuItem key={period.value} value={period.value}>
                {period.label || formatFilingPeriodLabel(period.value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Jump to month"
          type="month"
          size="small"
          value={filingPeriod || ''}
          onChange={(e) => handleFilingPeriodChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
          <Button
            variant="contained"
            color="primary"
            onClick={fetchInsights}
            startIcon={<Refresh />}
            size="small"
          >
            Refresh
          </Button>
          </Box>
      </Box>

      {/* Info alert about nexus connection */}
      <Alert 
        severity="info" 
        sx={{ mb: 3 }}
        icon={<InfoIcon />}
      >
        <AlertTitle>Connected to Economic Nexus Dashboard</AlertTitle>
        <Box sx={{ mt: 0 }}>
          <Typography variant="body2">
            These transactions are analyzed for nexus obligations across states. The nexus dashboard shows a 12-month lookback period for all transactions, 
            while this page shows data for the selected filing period. For consistent data between dashboards, we're now showing data from a 12-month lookback period.
                  </Typography>
          <Button
            component={Link}
            to={nexusPath}
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          >
            View Nexus Dashboard
          </Button>
                </Box>
      </Alert>

        {insights?.excluded_transactions > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {insights.excluded_transactions} transactions are excluded from this tax comparison until their tax calculation succeeds.
          </Alert>
        )}
        {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="transaction insights tabs">
          <Tab label="Actual Sales Tax Collected" id="insights-tab-0" />
          <Tab label="Expected Sales Tax Due" id="insights-tab-1" />
          <Tab label="Transaction Insights" id="insights-tab-2" />
        </Tabs>
      </Box>

      {/* Actual Sales Tax Collected Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Collected
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(insights?.actual_sales_tax?.total_collected)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Transactions
                </Typography>
                <Typography variant="h4">
                  {insights?.actual_sales_tax?.transaction_count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Average Tax Rate
                </Typography>
                <Typography variant="h4">
                  {(insights?.actual_sales_tax?.average_tax_rate || 0).toFixed(2)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Expected Sales Tax Due Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Due
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(insights?.expected_sales_tax?.total_due)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Transactions
                </Typography>
                <Typography variant="h4">
                  {insights?.expected_sales_tax?.transaction_count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Average Tax Rate
                </Typography>
                <Typography variant="h4">
                  {(insights?.expected_sales_tax?.average_tax_rate || 0).toFixed(2)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Transaction Insights Tab */}
      <TabPanel value={tabValue} index={2}>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Filing period
                    </Typography>
                    <Typography variant="h6">
                      {insights?.filing_period}
                    </Typography>
                  </Box>
                  <Receipt color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Compared transactions
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {insights?.transaction_insights?.new_transactions || 0}
                      </Typography>
                      {getTrendIcon(insights?.transaction_insights?.new_transactions_trend)}
                      <Typography variant="body2" color="textSecondary">
                        {formatTrend(insights?.transaction_insights?.new_transactions_trend)}
                      </Typography>
                    </Box>
                  </Box>
                  <Assessment color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Transactions with variance
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {insights?.transaction_insights?.transactions_with_variance || 0}
                      </Typography>
                      {getTrendIcon(insights?.transaction_insights?.variance_transactions_trend)}
                      <Typography variant="body2" color="textSecondary">
                        {formatTrend(insights?.transaction_insights?.variance_transactions_trend)}
                      </Typography>
                    </Box>
                  </Box>
                  <Warning color="warning" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Net variance amount
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {formatCurrency(insights?.transaction_insights?.net_variance_amount)}
                      </Typography>
                      {getTrendIcon(insights?.transaction_insights?.net_variance_trend)}
                      <Typography variant="body2" color="textSecondary">
                        {formatTrend(insights?.transaction_insights?.net_variance_trend)}
                      </Typography>
                    </Box>
                  </Box>
                  <AttachMoney color="success" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Variance Analysis */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Variance Analysis
            </Typography>
            
            {insights?.transaction_insights?.variance_breakdown?.length > 0 ? (
            <List>
                {insights.transaction_insights.variance_breakdown.map((item, index) => (
                  <React.Fragment key={item.variance_type || index}>
                    <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      {getVarianceIcon(item.variance_type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body1" fontWeight="medium">
                              {item.variance_type === 'under_collected' ? 'Under-collected transactions' :
                               item.variance_type === 'over_collected' ? 'Over-collected transactions' :
                               item.variance_type === 'penny_variance' ? 'Penny variance transactions' :
                               'Exact match transactions'}
                          </Typography>
                          <Box display="flex" gap={2}>
                            <Chip 
                              label={`${item.count} transactions`} 
                              size="small" 
                              variant="outlined"
                                color={item.variance_type === 'under_collected' ? 'warning' : 
                                       item.variance_type === 'over_collected' ? 'info' :
                                       item.variance_type === 'penny_variance' ? 'secondary' : 'success'}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Total Variance: {formatCurrency(item.amount || 0)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Recent transactions (click to view details):
                          </Typography>
                            <Button 
                              variant="outlined" 
                              size="small" 
                              onClick={() => fetchAllTransactions(item.variance_type)}
                              startIcon={<Visibility />}
                              sx={{ mb: 2 }}
                            >
                              View all {item.count} transactions
                            </Button>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < insights.transaction_insights.variance_breakdown.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            ) : (
              <Alert severity="info">
                No transaction variance data available for this period. Try selecting a different filing period.
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        open={detailsOpen}
        onClose={handleCloseDetails}
        transaction={selectedTransaction}
        loading={detailsLoading}
        onNavigateToCalculation={(transaction) => {
          // TODO: Navigate to tax calculation page
          logger.log('Navigate to calculation for transaction:', transaction.transaction_id);
        }}
      />

      {/* All Transactions Modal */}
      <AllTransactionsModal
        open={showAllTransactions}
        onClose={() => {
          setShowAllTransactions(false);
          setAllTransactionsVarianceType(null);
        }}
        transactions={allTransactions}
        onTransactionClick={handleTransactionClick}
        varianceType={allTransactionsVarianceType}
      />
    </Box>
  );
};

export default TransactionInsights; 

